import { useEffect, useState, type ChangeEvent, type ReactNode } from 'react';
import { AlertTriangle, Globe, Loader2, Lock, MessageSquareText, Pencil, Plus, Sparkles, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import Button from '../ui/Button';
import CultivarProfileView from './CultivarProfileView';
import cultivarService from '../../services/cultivar';
import { errorMessage } from '../../utils/apiResponse';
import { fromDraft, profileHasContent, toDraft, type ProfileDraft } from '../../utils/cultivarProfile';
import { formatDateInTz, useOrgTimezone } from '../../utils/timezone';
import type { Cultivar, CultivarInfo, CultivarInfoProposal, CultivarSalesProfile } from '../../types/cultivar';

/**
 * "What to tell a customer": a cultivar's talking points, so someone who does
 * not know the crop can read two varieties and speak clearly about both.
 *
 * A Cultivar Admin of the organisation that registered the cultivar can have
 * it researched. Research fills this form and saves nothing: somebody reads
 * it, corrects it and saves. The talking points are staff-only until "Show on
 * the public website" is ticked.
 */

type Found = Extract<CultivarInfoProposal, { found: true }>;

export interface CultivarInfoPanelProps {
  cultivar: Cultivar;
  /** pbr or admin seat, own cultivar. */
  canEdit: boolean;
  /** Cultivar Admin seat, own cultivar. */
  canResearch: boolean;
  onSaved: (info: CultivarInfo) => void;
}

const FIELD = 'w-full rounded-md border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-sm font-medium text-neutral-700">{label}</label>
      {hint && <p className="text-xs text-neutral-500 mb-1 break-words">{hint}</p>}
      <div className={hint ? '' : 'mt-1'}>{children}</div>
    </div>
  );
}

export default function CultivarInfoPanel({ cultivar, canEdit, canResearch, onSaved }: CultivarInfoPanelProps) {
  const orgTz = useOrgTimezone();
  const rootstock = cultivar.cultivar_role === 'rootstock';
  const saved = profileHasContent(cultivar.sales_profile) ? cultivar.sales_profile : null;

  const [editing, setEditing] = useState(false);
  const [description, setDescription] = useState('');
  const [tagline, setTagline] = useState('');
  const [draft, setDraft] = useState<ProfileDraft>(() => toDraft(null));
  const [isPublic, setIsPublic] = useState(false);
  // Where the text being edited came from: the saved profile, or a proposal.
  const [meta, setMeta] = useState<CultivarSalesProfile | null>(null);
  const [proposal, setProposal] = useState<Found | null>(null);

  const [researching, setResearching] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState<string | null>(null);

  useEffect(() => {
    if (!researching) return;
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [researching]);

  const openEditor = (from: { description: string | null; tagline: string | null; profile: CultivarSalesProfile | null }) => {
    setDescription(from.description ?? '');
    setTagline(from.tagline ?? '');
    setDraft(toDraft(from.profile));
    setMeta(from.profile);
    setIsPublic(!!cultivar.sales_profile_public);
    setError(null);
    setEditing(true);
  };

  const edit = () => {
    setProposal(null);
    openEditor({ description: cultivar.description, tagline: cultivar.website_tagline ?? null, profile: saved });
  };

  const research = async () => {
    setResearching(true);
    setError(null);
    setNotFound(null);
    try {
      const res = await cultivarService.researchCultivarInfo(cultivar.id);
      if (!res.success || !res.data) { setError(errorMessage(res, 'The research could not be run')); return; }
      if (!res.data.found) { setNotFound(res.data.message); return; }
      setProposal(res.data);
      openEditor({
        description: res.data.description ?? cultivar.description,
        tagline: res.data.website_tagline ?? cultivar.website_tagline ?? null,
        profile: res.data.profile,
      });
    } catch (e) {
      setError(errorMessage(e, 'The research could not be run'));
    } finally {
      setResearching(false);
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await cultivarService.saveCultivarInfo(cultivar.id, {
        description: description.trim() || null,
        website_tagline: tagline.trim() || null,
        sales_profile: fromDraft(draft, meta),
        sales_profile_public: isPublic,
      });
      if (!res.success || !res.data) { setError(errorMessage(res, 'Could not save')); return; }
      onSaved(res.data);
      setEditing(false);
      setProposal(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const set = (key: Exclude<keyof ProfileDraft, 'compared_with'>) =>
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => setDraft((d) => ({ ...d, [key]: e.target.value }));
  const setCompared = (index: number, patch: { name?: string; difference?: string }) =>
    setDraft((d) => ({ ...d, compared_with: d.compared_with.map((c, i) => (i === index ? { ...c, ...patch } : c)) }));

  // Someone with neither a profile to read nor a way to write one sees nothing.
  if (!saved && !canEdit && !canResearch) return null;

  const researchButton = canResearch && (
    <Button variant="outline" size="sm" onClick={research} disabled={researching || saving}>
      {researching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
      {saved || editing ? 'Research again' : 'Research this cultivar'}
    </Button>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2">
              <MessageSquareText className="h-5 w-5 text-primary-600 flex-shrink-0" />
              What to tell a customer
            </CardTitle>
            {saved && !editing && (
              <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-neutral-500">
                {cultivar.sales_profile_public
                  ? <><Globe className="h-3.5 w-3.5" /> Shown on the public website</>
                  : <><Lock className="h-3.5 w-3.5" /> Staff only</>}
              </p>
            )}
          </div>
          {!editing && (
            <div className="flex flex-wrap gap-2">
              {researchButton}
              {canEdit && (
                <Button variant="outline" size="sm" onClick={edit} disabled={researching}>
                  <Pencil className="h-4 w-4" />
                  {saved ? 'Edit' : 'Write them'}
                </Button>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {researching && (
          <div className="mb-4 rounded-lg border border-primary-200 bg-primary-50 px-3 py-2 text-sm text-primary-900" role="status">
            Researching {cultivar.name}. This usually takes one to two minutes ({elapsed}s). Nothing is saved until you have read it.
          </div>
        )}
        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 break-words" role="alert">{error}</div>
        )}
        {notFound && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 break-words">
            <p className="font-medium">Not enough was found to describe this cultivar.</p>
            <p>{notFound}</p>
          </div>
        )}

        {!editing && (saved ? (
          <>
            <CultivarProfileView profile={saved} />
            {(saved.researched_at || saved.reviewed_at) && (
              <p className="mt-4 pt-3 border-t border-neutral-100 text-xs text-neutral-500 break-words">
                {saved.researched_at && <>Researched by AI on {formatDateInTz(saved.researched_at, orgTz)}. </>}
                {saved.reviewed_at && <>Last checked and saved by a person on {formatDateInTz(saved.reviewed_at, orgTz)}.</>}
              </p>
            )}
            {canEdit && (saved.sources?.length ?? 0) > 0 && (
              <details className="mt-2 text-xs text-neutral-600">
                <summary className="cursor-pointer">Sources ({saved.sources?.length})</summary>
                <ul className="mt-1 space-y-1">
                  {saved.sources?.map((s) => (
                    <li key={s.url} className="break-all">
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-primary-700 hover:underline">{s.title}</a>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        ) : !researching && (
          <p className="text-sm text-neutral-600 break-words">
            No talking points yet: what the {rootstock ? 'rootstock does for the tree' : 'fruit is like'}, when it crops, who it suits
            and how it differs from similar cultivars.{' '}
            {canResearch
              ? 'Research it, read what comes back, and save it.'
              : 'A Cultivar Admin can have them researched.'}
          </p>
        ))}

        {editing && (
          <div className="space-y-4">
            {proposal && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                <p className="flex items-start gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Researched by AI. Review before saving: it can be wrong while sounding sure. Nothing is saved yet.</span>
                </p>
                {proposal.warnings.length > 0 && (
                  <ul className="mt-2 list-disc pl-9 space-y-1">
                    {proposal.warnings.map((w) => <li key={w} className="break-words">{w}</li>)}
                  </ul>
                )}
                {proposal.compared_against.length > 0 && (
                  <p className="mt-2 pl-6 text-xs break-words">
                    Compared against the cultivars you hold: {proposal.compared_against.map((c) => c.name).join(', ')}.
                  </p>
                )}
                {(proposal.profile.sources?.length ?? 0) > 0 && (
                  <ul className="mt-2 pl-6 space-y-0.5 text-xs">
                    {proposal.profile.sources?.map((s) => (
                      <li key={s.url} className="break-all">
                        <a href={s.url} target="_blank" rel="noreferrer" className="underline">{s.title}</a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <Field id="info-description" label="Description" hint="Two or three sentences for a customer. This is the cultivar's description everywhere, including the public website.">
              <textarea id="info-description" rows={4} maxLength={4000} value={description} onChange={(e) => setDescription(e.target.value)} className={FIELD} />
            </Field>
            <Field id="info-tagline" label="Tagline" hint="One short line for a catalogue card.">
              <input id="info-tagline" type="text" maxLength={200} value={tagline} onChange={(e) => setTagline(e.target.value)} className={FIELD} />
            </Field>

            <div className="border-t border-neutral-100 pt-4 space-y-4">
              <Field id="info-summary" label="In one sentence" hint="What a salesperson could say out loud.">
                <textarea id="info-summary" rows={2} maxLength={400} value={draft.summary} onChange={set('summary')} className={FIELD} />
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!rootstock && (
                  <Field id="info-fruit" label="Fruit">
                    <textarea id="info-fruit" rows={3} maxLength={800} value={draft.fruit} onChange={set('fruit')} className={FIELD} />
                  </Field>
                )}
                <Field id="info-season" label="Season">
                  <textarea id="info-season" rows={3} maxLength={800} value={draft.season} onChange={set('season')} className={FIELD} />
                </Field>
                <Field id="info-tree" label="Tree">
                  <textarea id="info-tree" rows={3} maxLength={800} value={draft.tree} onChange={set('tree')} className={FIELD} />
                </Field>
                <Field id="info-growing" label="Growing">
                  <textarea id="info-growing" rows={3} maxLength={800} value={draft.growing} onChange={set('growing')} className={FIELD} />
                </Field>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Field id="info-best-for" label="Best for" hint="One per line.">
                  <textarea id="info-best-for" rows={4} value={draft.best_for} onChange={set('best_for')} className={FIELD} />
                </Field>
                <Field id="info-selling" label="Selling points" hint="One per line.">
                  <textarea id="info-selling" rows={4} value={draft.selling_points} onChange={set('selling_points')} className={FIELD} />
                </Field>
                <Field id="info-watch" label="Tell the customer" hint="Drawbacks, one per line.">
                  <textarea id="info-watch" rows={4} value={draft.watch_outs} onChange={set('watch_outs')} className={FIELD} />
                </Field>
              </div>

              <div>
                <p className="text-sm font-medium text-neutral-700">How it differs from similar cultivars</p>
                <ul className="mt-2 space-y-2">
                  {draft.compared_with.map((c, i) => (
                    // Keyed by position: nothing else identifies a row that is still blank.
                    <li key={i} className="flex items-start gap-2">
                      <div className="grid flex-1 min-w-0 grid-cols-1 sm:grid-cols-[10rem_minmax(0,1fr)] gap-2">
                        <input
                          type="text" aria-label={`Cultivar ${i + 1}`} placeholder="Cultivar" maxLength={160}
                          value={c.name} onChange={(e) => setCompared(i, { name: e.target.value })} className={FIELD}
                        />
                        <input
                          type="text" aria-label={`How it differs from cultivar ${i + 1}`} placeholder="How this one differs" maxLength={500}
                          value={c.difference} onChange={(e) => setCompared(i, { difference: e.target.value })} className={FIELD}
                        />
                      </div>
                      <button
                        type="button" aria-label={`Remove comparison ${i + 1}`}
                        onClick={() => setDraft((d) => ({ ...d, compared_with: d.compared_with.filter((_, j) => j !== i) }))}
                        className="mt-1.5 p-1 rounded text-neutral-500 hover:bg-neutral-100 flex-shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
                {draft.compared_with.length < 6 && (
                  <button
                    type="button"
                    onClick={() => setDraft((d) => ({ ...d, compared_with: [...d.compared_with, { cultivar_id: null, name: '', difference: '' }] }))}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800"
                  >
                    <Plus className="h-4 w-4" /> Add a comparison
                  </button>
                )}
              </div>
            </div>

            <label className="flex items-start gap-2 border-t border-neutral-100 pt-4 text-sm text-neutral-800">
              <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500" />
              <span className="min-w-0 break-words">
                Show these talking points on the public website
                <span className="block text-xs text-neutral-500">
                  {cultivar.show_on_website
                    ? 'Left unticked, only staff see them. The description above is on the website either way.'
                    : 'This cultivar is not on the website yet. They will show once it is published.'}
                </span>
              </span>
            </label>

            <div className="flex flex-wrap justify-end gap-2">
              {researchButton}
              <Button variant="outline" size="sm" onClick={() => { setEditing(false); setProposal(null); setError(null); }} disabled={saving || researching}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" onClick={save} disabled={saving || researching || !canEdit}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
